import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, Pencil, Code2, Eye } from "lucide-react";

const FIELDS = [
  { key: "identity", label: "Identity", type: "text" },
  { key: "niche", label: "Niche", type: "text" },
  { key: "audience", label: "Audience", type: "textarea" },
  { key: "platform", label: "Primary Platform", type: "text" },
  { key: "personality", label: "Personality / Tone", type: "textarea" },
  { key: "desired_action", label: "Desired Action", type: "text" },
  { key: "topics", label: "Content Topics", type: "textarea" },
  { key: "positioning", label: "Positioning Statement", type: "textarea" },
];

export default function DiveBrain() {
  const [brain, setBrain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});
  const [showJson, setShowJson] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dive-brain");
        setBrain(data.brain);
        setDraft(data.brain || {});
        if (params.get("fresh")) toast.success("Your Dive Brain has been generated");
      } finally {
        setLoading(false);
      }
    })();
  }, [params]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach(({ key }) => { if (draft[key] !== undefined) payload[key] = draft[key]; });
      if (Array.isArray(draft.tone_keywords)) payload.tone_keywords = draft.tone_keywords;
      if (Array.isArray(draft.content_pillars)) payload.content_pillars = draft.content_pillars;
      const { data } = await api.put("/dive-brain", payload);
      setBrain(data.brain);
      setEditing(false);
      toast.success("Dive Brain updated");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-[#64748B]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading your Dive Brain…</div>;

  return (
    <div data-testid="dive-brain-page" className="px-6 lg:px-10 py-10 max-w-5xl">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="dms-label mb-3">Your identity layer</div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342]">Dive Brain</h1>
          <p className="mt-2 text-[#475569] max-w-2xl">Powering every output. Edit anything below; saves are instant.</p>
        </div>
        {!editing ? (
          <div className="flex gap-2">
            <button data-testid="brain-toggle-json" onClick={() => setShowJson((v) => !v)} className="dms-btn-ghost">
              {showJson ? <><Eye className="w-4 h-4" /> Readable</> : <><Code2 className="w-4 h-4" /> JSON</>}
            </button>
            <button data-testid="brain-edit" onClick={() => setEditing(true)} className="dms-btn-ghost"><Pencil className="w-4 h-4" /> Edit</button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button data-testid="brain-cancel" onClick={() => { setDraft(brain || {}); setEditing(false); }} className="dms-btn-ghost">Cancel</button>
            <button data-testid="brain-save" onClick={save} disabled={saving} className="dms-btn-primary"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</button>
          </div>
        )}
      </div>

      {showJson && !editing ? (
        <div className="dms-card p-5 sm:p-6" data-testid="brain-json-view">
          <div className="dms-label mb-3">Full Dive Brain (JSON)</div>
          <pre className="text-[12.5px] leading-relaxed text-[#1F2937] whitespace-pre-wrap font-mono overflow-x-auto">{JSON.stringify(brain, null, 2)}</pre>
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="brain-readable-view">
        {FIELDS.map(({ key, label, type }) => (
          <div key={key} className="dms-card p-5">
            <div className="dms-label mb-2">{label}</div>
            {editing ? (
              type === "textarea" ? (
                <textarea data-testid={`brain-input-${key}`} value={draft[key] || ""} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} className="dms-textarea" rows={3} />
              ) : (
                <input data-testid={`brain-input-${key}`} value={draft[key] || ""} onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))} className="dms-input" />
              )
            ) : (
              <div data-testid={`brain-value-${key}`} className="text-[15px] leading-relaxed text-[#1F2937] whitespace-pre-wrap">{brain?.[key] || "-"}</div>
            )}
          </div>
        ))}

        <div className="dms-card p-5 md:col-span-2">
          <div className="dms-label mb-3">Goals</div>
          {Array.isArray(brain?.goals) && brain.goals.length ? (
            <div className="flex flex-wrap gap-2">
              {brain.goals.map((g, i) => <span key={i} className="dms-badge">{g}</span>)}
            </div>
          ) : <div className="text-[#64748B] text-sm">-</div>}
        </div>

        <div className="dms-card p-5 md:col-span-2">
          <div className="dms-label mb-3">Content Pillars</div>
          {Array.isArray(brain?.content_pillars) && brain.content_pillars.length ? (
            <div className="flex flex-wrap gap-2">
              {brain.content_pillars.map((p, i) => <span key={i} className="dms-badge">{p}</span>)}
            </div>
          ) : <div className="text-[#64748B] text-sm">-</div>}
        </div>

        <div className="dms-card p-5 md:col-span-2">
          <div className="dms-label mb-3">Tone Keywords</div>
          {Array.isArray(brain?.tone_keywords) && brain.tone_keywords.length ? (
            <div className="flex flex-wrap gap-2">
              {brain.tone_keywords.map((p, i) => <span key={i} className="dms-badge">{p}</span>)}
            </div>
          ) : <div className="text-[#64748B] text-sm">-</div>}
        </div>
      </div>
      )}
    </div>
  );
}
